import axios from 'axios';
import { 
  Rule, 
  CreateRuleRequest, 
  UpdateRuleRequest, 
  RuleFilters, 
  RuleTestResult,
  RuleExecutionResult 
} from '../types/rules';
import { LOAN_PROCESSING_RULES } from '@/data/loanRulesTemplates';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const USE_MOCK_DATA = import.meta.env.VITE_ENVIRONMENT === 'development' && !import.meta.env.VITE_USE_REAL_API;

class RulesService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getRules(filters?: RuleFilters): Promise<{ rules: Rule[]; total: number }> {
    if (USE_MOCK_DATA) {
      return {rules: LOAN_PROCESSING_RULES, total: LOAN_PROCESSING_RULES.length};
    }
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.tag) params.append('tag', filters.tag);

    const response = await axios.get(
      `${API_BASE_URL}/rules${params.toString() ? `?${params}` : ''}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getRule(id: string): Promise<Rule> {
    const response = await axios.get(
      `${API_BASE_URL}/rules/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createRule(rule: CreateRuleRequest): Promise<Rule> {
    const response = await axios.post(
      `${API_BASE_URL}/rules`,
      rule,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateRule(id: string, updates: UpdateRuleRequest): Promise<Rule> {
    const response = await axios.put(
      `${API_BASE_URL}/rules/${id}`,
      updates,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteRule(id: string): Promise<void> {
    await axios.delete(
      `${API_BASE_URL}/rules/${id}`,
      { headers: this.getAuthHeaders() }
    );
  }

  async testRule(id: string, input: Record<string, unknown>): Promise<RuleTestResult> {
    const response = await axios.post(
      `${API_BASE_URL}/rules/${id}/test`,
      { input },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async executeRules(trigger: string, context: Record<string, unknown>): Promise<{
    trigger: string;
    executedRules: number;
    results: RuleExecutionResult[];
    timestamp: string;
  }> {
    const response = await axios.post(
      `${API_BASE_URL}/rules/execute`,
      { trigger, context },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getRuleHistory(id: string): Promise<{
    ruleId: string;
    history: Array<{
      id: string;
      action: string;
      timestamp: string;
      userId: string;
      details: Record<string, unknown>;
    }>;
  }> {
    const response = await axios.get(
      `${API_BASE_URL}/rules/${id}/history`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export const rulesService = new RulesService();
export default rulesService;